'use client'

import { useTsrClient } from '../../end-users/tsr'
import { Card, CardContent, CardHeader, CardTitle } from '@alphabait/ui' // card'
import { Button } from '@alphabait/ui'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@alphabait/ui'
import { Checkbox } from '@alphabait/ui' // checkbox'
import { AlertTriangle, Download } from 'lucide-react'
import { useState } from 'react'
import { useRecoilState } from 'recoil'
import { useTranslation } from 'react-i18next'
import { tokenState } from '../../../store/atoms/token'
import {
  Cell,
  Pie,
  PieChart as RechartsChart,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { ChartTooltip } from '@alphabait/ui' // chart'
import { ChartErrorState } from '@/components/charts/question-renderers/shared/chart-error-state'
import { ChartLoadingState } from '@/components/charts/question-renderers/shared/chart-loading-state'
import { ChartEmptyState } from '@/components/charts/question-renderers/shared/chart-empty-state'

interface RiskFactorsProps {
  periodStart: string
  periodEnd: string
  customerId?: string
  campaignIds?: string[]
  campaignGroupId?: string
}

export function RiskFactors({
  periodStart,
  periodEnd,
  customerId,
  campaignIds,
  campaignGroupId,
}: RiskFactorsProps) {
  const { t } = useTranslation()
  const tsr = useTsrClient()
  const [idToken] = useRecoilState(tokenState)

  // State for export options
  const [exportRiskTypes, setExportRiskTypes] = useState<string[]>([
    'neverOpened',
    'lowHappiness',
    'nonResponders',
  ])
  const [isExporting, setIsExporting] = useState(false)

  // Convert dates to timestamps for the API
  const startTimestamp = new Date(periodStart).getTime() / 1000
  const endTimestamp = new Date(periodEnd).getTime() / 1000

  const {
    data: riskData,
    isPending: loadingRisk,
    error: riskError,
  } = tsr.dashboard.getRiskFactors.useQuery({
    queryKey: [
      'risk-factors',
      periodStart,
      periodEnd,
      customerId,
      campaignIds,
      campaignGroupId,
    ],
    queryData: {
      query: {
        startDate: startTimestamp,
        endDate: endTimestamp,
        customerId,
        campaignIds,
        campaignGroupId,
      },
    },
  })

  const risk = riskData?.body

  if (loadingRisk) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            {t('dashboard.risk_factors.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ChartLoadingState />
        </CardContent>
      </Card>
    )
  }

  if (riskError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            {t('dashboard.risk_factors.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ChartErrorState message={t('dashboard.charts.failed')} />
        </CardContent>
      </Card>
    )
  }

  if (!risk || risk.totalUsers === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            {t('dashboard.risk_factors.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center min-h-[400px]">
          <ChartEmptyState message={t('dashboard.risk_factors.no_risk_data')} />
        </CardContent>
      </Card>
    )
  }

  // Prepare data for the pie chart
  const chartData = [
    {
      name: t('dashboard.risk_factors.never_opened'),
      value: risk.riskGroups.neverOpened.count,
      percentage: risk.riskGroups.neverOpened.percentage,
      color: 'hsl(var(--base-chart-1))', // primary chart color
      description: risk.riskGroups.neverOpened.description,
    },
    {
      name: t('dashboard.risk_factors.low_satisfaction'),
      value: risk.riskGroups.lowHappiness.count,
      percentage: risk.riskGroups.lowHappiness.percentage,
      color: 'hsl(var(--Secondary-light-blue-4))', // light purple
      description: risk.riskGroups.lowHappiness.description,
    },
    {
      name: t('dashboard.risk_factors.non_responders'),
      value: risk.riskGroups.nonResponders.count,
      percentage: risk.riskGroups.nonResponders.percentage,
      color: 'hsl(var(--base-chart-3))', // light blue
      description: risk.riskGroups.nonResponders.description,
    },
    {
      name: t('dashboard.risk_factors.safe_users'),
      value: risk.totalUsers - risk.summary.totalAtRisk,
      percentage: 100 - risk.summary.riskPercentage,
      color: 'hsl(var(--Secondary-light-blue-2))', // very light
      description: t('dashboard.risk_factors.safe_users_desc'),
    },
  ].filter((item) => item.value > 0) // Only show segments with data

  // Export CSV function
  const handleExportCSV = async () => {
    if (exportRiskTypes.length === 0) {
      alert(t('dashboard.risk_factors.select_at_least_one'))
      return
    }

    if (!idToken?.__raw) {
      alert(t('dashboard.risk_factors.auth_required'))
      return
    }

    setIsExporting(true)
    try {
      const baseUrl = window.location.origin
      const url =
        `${baseUrl}/v2/dashboard/risk-factors/export?` +
        new URLSearchParams({
          startDate: startTimestamp.toString(),
          endDate: endTimestamp.toString(),
          ...(customerId && { customerId }),
          riskTypes: exportRiskTypes.join(','),
        })

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken.__raw}`,
          Accept: 'text/csv,text/plain,*/*',
        },
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Error response:', errorText)
        throw new Error(
          `Failed to export CSV: ${response.status} ${response.statusText}`,
        )
      }

      const csvContent = await response.text()

      if (!csvContent || csvContent.trim().length === 0) {
        throw new Error(t('dashboard.risk_factors.empty_csv'))
      }

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const downloadUrl = URL.createObjectURL(blob)
      link.setAttribute('href', downloadUrl)
      link.setAttribute(
        'download',
        `risk-factors-${new Date().toISOString().split('T')[0]}.csv`,
      )
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(downloadUrl)
    } catch (error: unknown) {
      console.error('Error exporting CSV:', error)
      alert(
        t('dashboard.risk_factors.export_failed', {
          error: error instanceof Error ? error.message : 'Unknown error',
        }),
      )
    } finally {
      setIsExporting(false)
    }
  }

  // Handle risk type selection for export
  const handleRiskTypeToggle = (riskType: string) => {
    setExportRiskTypes((prev) =>
      prev.includes(riskType)
        ? prev.filter((type) => type !== riskType)
        : [...prev, riskType],
    )
  }

  const riskTypeLabels = {
    neverOpened: t('dashboard.risk_factors.never_opened'),
    lowHappiness: t('dashboard.risk_factors.low_satisfaction'),
    nonResponders: t('dashboard.risk_factors.non_responders'),
  }

  // Custom tooltip for the pie chart
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean
    payload?: Array<{
      payload: {
        name: string
        description: string
        value: number
        percentage: number
      }
    }>
  }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div
          className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg opacity-100 z-50"
          style={{ backgroundColor: 'white', opacity: 1, zIndex: 9999 }}
        >
          <p className="font-semibold text-gray-900">{data.name}</p>
          <p className="text-sm text-gray-600">{data.description}</p>
          <p className="text-sm font-medium">
            {t('dashboard.risk_factors.count_label')}{' '}
            <span className="font-bold">{data.value.toLocaleString()}</span>
          </p>
          <p className="text-sm font-medium">
            {t('dashboard.risk_factors.percentage_label')}{' '}
            <span className="font-bold">{data.percentage.toFixed(1)}%</span>
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {t('dashboard.risk_factors.title')}
            </CardTitle>
            <p
              className="text-sm text-muted-foreground"
              style={{ zIndex: -100 }}
            >
              {t('dashboard.risk_factors.subtitle', { count: risk.totalUsers })}
            </p>
          </div>

          {/* Export Controls */}
          <div className="flex items-center gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full">
                  <Download className="h-4 w-4 mr-2" />
                  {t('dashboard.risk_factors.export_options')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-gray-30 font-semibold leading-none mt-2">
                    {t('dashboard.risk_factors.export_dialog_title')}
                    <p className="text-sm font-thin text-gray-50 my-2">
                      {t('dashboard.risk_factors.select_risk_types')}
                    </p>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-3 mt-2 mb-8">
                    {Object.entries(riskTypeLabels).map(([key, label]) => (
                      <div key={key} className="flex items-center space-x-2">
                        <Checkbox
                          id={key}
                          checked={exportRiskTypes.includes(key)}
                          onCheckedChange={() => handleRiskTypeToggle(key)}
                          className="bg-white"
                        />
                        <label
                          htmlFor={key}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {label}
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end pt-8">
                    <Button
                      onClick={handleExportCSV}
                      disabled={isExporting || exportRiskTypes.length === 0}
                      className="text-white"
                    >
                      {isExporting ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      {t('dashboard.risk_factors.export_csv')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col justify-between min-h-[550px]">
        {/* Interactive Pie Chart */}
        <div className="relative h-[400px] my-4">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                innerRadius={80}
                outerRadius={160}
                fill="#8884d8"
                dataKey="value"
                strokeWidth={2}
                stroke="#fff"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <ChartTooltip content={<CustomTooltip />} />
              <Legend
                layout="horizontal"
                align="center"
                verticalAlign="bottom"
                wrapperStyle={{
                  paddingTop: '20px',
                  width: '80%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginLeft: '10%',
                  marginRight: '10%',
                }}
                formatter={(value) => {
                  const data = chartData.find((item) => item.name === value)
                  return (
                    <span className="text-sm font-medium text-secondary-foreground">
                      {value} ({data?.percentage?.toFixed(1) || 0}%)
                    </span>
                  )
                }}
              />
            </RechartsChart>
          </ResponsiveContainer>
        </div>
        {/* Summary Stats */}
        <div className="grid gap-2 mt-auto">
          <div className="flex rounded-lg  bg-amber-50 px-4 py-1 justify-between items-center">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <p className="font-semibold text-amber-900">
                {t('dashboard.risk_factors.total_at_risk')}
              </p>
            </div>
            <div className="text-2xl font-bold text-amber-600 flex ">
              {/* {`(${risk.summary.riskPercentage}%)`}{' '} */}
              <p className="font-bold text-amber-800">
                {risk.summary.totalAtRisk.toLocaleString()}{' '}
                <span className="text-sm font-normal">
                  {t('dashboard.risk_factors.of_total_users')}
                </span>{' '}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
